import { selMessage } from "@selector/shared";
import { DB_FIELD_MAX_LENGTH } from "@selector/shared/settings";
import { type FC, useEffect } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { formValueSelector, type InjectedFormProps, reduxForm, SubmissionError } from "redux-form";
import { HStack, InfoToggletip, TriggerButton } from "widgets";
import { usePrevious } from "widgets/hooks";

import { buildSavedFlagMessage } from "@/app-utils/alert.tsx";
import { prjPrivateSavingAllowed } from "@/app-utils/capability.ts";
import { getPaddedRange } from "@/app-utils/range.ts";
import {
  type Contig,
  type InputRegionFlagInput,
  type Organism,
  type Project,
  type Range,
  type Region,
  type UserGroup,
} from "@/graphql.ts";
import { notify } from "@/modules/notifications/notify.tsx";
import { loadUserParams } from "@/redux/modules/auth.ts";
import { getProjectLabel } from "@/redux/modules/config.ts";
import { showFlagsStateChanged, zoomToRegion } from "@/redux/modules/genome-browser.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { type ReduxDispatch } from "@/redux/redux-utils.ts";
import { UNKNOWN_GENOME_ID } from "@/settings/settings.js";
import ColorPickerField, { ColorPickerReduxFormField } from "@/ui-modules/core/redux-form/ColorPickerField.tsx";
import { ProjectTagInputReduxFormField } from "@/ui-modules/core/redux-form/ProjectTagInputField.tsx";
import {
  RegionSelectorReduxFormField,
  validateRegionInputField,
} from "@/ui-modules/core/redux-form/RegionSelectorField.tsx";

import ProjectsSelect from "../../core/forms/ProjectsSelect.js";
import { Dialog } from "../../core/overlays/Dialogs.js";
import { PopOverActionWidget } from "../../core/overlays/index.js";
import CheckboxField, { CheckboxReduxFormField } from "../../core/redux-form/CheckboxField.js";
import InputField, { InputReduxFormField } from "../../core/redux-form/InputField.js";
import { useCreateRegionFlag } from "./useFlagGraphql.js";

interface SaveFlagDialogProps extends InjectedFormProps<any, any> {
  onClose: () => void;
  range: Range;
  contig: Contig;
  userGroups: UserGroup[]; // TODO: from Global Store?
}

const FORM_NAME = "SAVE_FLAG_FORM";
const FIELD_FLAG_NAME = "FLAG_NAME";
const FIELD_FLAG_DESCRIPTION = "FLAG_DESCRIPTION";
const FIELD_MAKEPRIVATE = "MAKEPRIVATE";
const FIELD_DESTINATION_PROJECT = "DESTINATION_PROJECT";
const FIELD_REGION = "REGION";
const FIELD_COLOR = "COLOR";
const FIELD_PROJECT_TAGS = "PROJECT_FLAGS";

const SaveFlagDialogClass: FC<SaveFlagDialogProps> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();
  const selectedProject = useSelector<IAppState, Project>((state) =>
    formValueSelector(FORM_NAME)(state, FIELD_DESTINATION_PROJECT),
  );
  const genome = useSelector<IAppState, Organism>((state) => state.genomeBrowser.genome, shallowEqual);

  const isPrivateWorkspace = selectedProject?.isPrivateWorkspace ?? true;
  const privateSavingAllowed = selectedProject && prjPrivateSavingAllowed(selectedProject);
  const [saveFlag, { loading }] = useCreateRegionFlag();

  // const handleSubmitData = (values): Promise<CreateRegionFlagMutation> => {
  const handleSubmitData = (values): Promise<void> => {
    const region: Region = values[FIELD_REGION];

    const regionFlagInput: InputRegionFlagInput = {
      projectId: values[FIELD_DESTINATION_PROJECT].id,
      shared: !values[FIELD_MAKEPRIVATE],
      description: values[FIELD_FLAG_DESCRIPTION]?.trim(),
      name: values[FIELD_FLAG_NAME]?.trim(),
      contigId: region.contig.id,
      rangeStart: region.range.start,
      rangeStop: region.range.stop,
      color: values[FIELD_COLOR],
      itemTagIds: values[FIELD_PROJECT_TAGS],
    };

    return saveFlag({ input: regionFlagInput })
      .then(() => {
        // In order to update the last used project.
        void dispatch(loadUserParams());

        notify.info(
          {
            title: selMessage({ id: "flag.saved.successTitle", values: { flagsCount: 1 } }),
            children: buildSavedFlagMessage(regionFlagInput.name ?? "Genome Flag", () => {
              dispatch(showFlagsStateChanged(true));
              const paddedRange = getPaddedRange(region.range, region.contig.length);
              dispatch(zoomToRegion({ ...region, range: paddedRange }));
            }),
          },
          { autoClose: 10000 },
        );

        props.onClose();
      })
      .catch((error) => {
        // show the error directly in the dialog
        throw new SubmissionError({ _error: error.message });
      });
  };

  const previousPrivateWorkspace = usePrevious(isPrivateWorkspace);

  useEffect(() => {
    if (isPrivateWorkspace) {
      // if the Private Workspace has been selected, we set the "make private" checkbox accordingly
      props.change(FIELD_MAKEPRIVATE, true);
    } else if (!privateSavingAllowed) {
      props.change(FIELD_MAKEPRIVATE, false);
    } else if (previousPrivateWorkspace) {
      // if we select something else after the Private Workspace was selected then we uncheck the "Private"
      // checkbox to have the default behaviour again
      props.change(FIELD_MAKEPRIVATE, false);
    }
  }, [isPrivateWorkspace, previousPrivateWorkspace, privateSavingAllowed]);

  useEffect(() => {
    if (selectedProject != undefined) {
      // First init as the project will be set to the last used target.
      // Need to be async, or it ends up to an infinite loop
      setTimeout(() => {
        props.asyncValidate();
      }, 0);
    }
  }, [selectedProject]);

  return (
    <Dialog
      closeFn={props.onClose}
      mainActionButtonProps={{
        onPress: props.handleSubmit(handleSubmitData),
        isDisabled: props.submitting || !!props.error,
        children: "Save",
        isLoading: loading,
      }}
      footerMessage={{ message: props.error }}
    >
      <ProjectsSelect name={FIELD_DESTINATION_PROJECT} title={"Save to"} onInitToLastTarget={props.change} />

      <CheckboxReduxFormField
        name={FIELD_MAKEPRIVATE}
        label="Make private"
        disabled={selectedProject?.isPrivateWorkspace ?? true}
        component={CheckboxField}
        style={{ marginTop: 0 }}
      />

      <InputReduxFormField
        componentClass="input"
        name={FIELD_FLAG_NAME}
        label="Name"
        placeholder={`Optional name`}
        maxLength={DB_FIELD_MAX_LENGTH.FLAG.NAME}
        component={InputField}
      />

      <InputReduxFormField
        componentClass="textarea"
        name={FIELD_FLAG_DESCRIPTION}
        label="Description"
        placeholder="Optional description"
        maxLength={DB_FIELD_MAX_LENGTH.FLAG.DESCRIPTION}
        component={InputField}
      />

      <RegionSelectorReduxFormField
        name={FIELD_REGION}
        title={"Location"}
        genomeId={genome.id === UNKNOWN_GENOME_ID ? null : genome.id}
        horizontalLayout={false}
        isDisabled={false}
        validate={validateRegionInputField}
        enableInputsWithoutGenome={true}
      />

      <ProjectTagInputReduxFormField name={FIELD_PROJECT_TAGS} label="Tags" projectId={selectedProject?.id} />

      <ColorPickerReduxFormField
        name={FIELD_COLOR}
        title={
          <HStack>
            Color
            <InfoToggletip
              content={`To use this color in the Genome Viewer, select "Flag Color" in the  track configuration`}
            />
          </HStack>
        }
        component={ColorPickerField}
      />
    </Dialog>
  );
};

const validate = (values, props: SaveFlagDialogProps) => {
  const selectedProject: Project = values[FIELD_DESTINATION_PROJECT];

  // The user groups attached to the contig are derived from the related genome.
  if (
    selectedProject?.userGroup &&
    !selectedProject.isPrivateWorkspace &&
    !props.userGroups.find((ug: UserGroup) => ug.id === selectedProject.userGroup.id)
  ) {
    return {
      _error: `The flag cannot be saved to '${
        selectedProject.name
      }' due to the user group settings. Please select a different ${getProjectLabel(false, false)}.`,
    };
  }

  return {};
};

const SaveFlagDialog = (props) => {
  return (
    <SaveFlagDialogForm initialValues={{ [FIELD_REGION]: { contig: props.contig, range: props.range } }} {...props} />
  );
};

const SaveFlagDialogForm = reduxForm({
  form: FORM_NAME,
  validate,
})(SaveFlagDialogClass);

interface SaveFlagButtonProps {
  isDisabled?: boolean;
  range: Range;
  contig: Contig;
  userGroups: UserGroup[]; // TODO: from Global Store?
}

const SaveFlagButton: FC<SaveFlagButtonProps> = (props) => {
  return (
    <PopOverActionWidget
      title={"Save Flag"}
      actionWidget={({ open }) => (
        <TriggerButton
          id={"genome-viewer-flag-button"}
          isDisabled={props.isDisabled}
          tooltip={"Create a genome flag for the selected region"}
          onPress={() => open()}
          icon={"flag"}
          label={"Flag"}
          showLabel
        />
      )}
    >
      {({ close }) => <SaveFlagDialog {...props} onClose={close} />}
    </PopOverActionWidget>
  );
};

export default SaveFlagButton;
